// src/router/index.js
import Vue from 'vue'
import VueRouter from 'vue-router'

// 이동할 페이지 컴포넌트들 임포트
import LoginPage from '../views/LoginPage.vue'
import RegisterPage from '../views/RegisterPage.vue'
import InvoicePage from '../views/InvoicePage.vue'
import ProviderPage from '../views/base/ProviderPage.vue'
import CustomerPage from '../views/base/CustomerPage.vue'
import MyPage from '../views/MyPage.vue'
import ResetPasswordPage from '../views/ResetPasswordPage.vue'
import MainDashboardPage from '../views/MainDashboard.vue'
import AccountBookPage from '../views/AccountBookPage.vue'

Vue.use(VueRouter)

import axios from 'axios'

const API_BASE_URL = window.location.origin;

// Axios 인스턴스 생성 (이것을 전역에서 사용하도록 설정)
const apiClient = axios.create({
    baseURL: API_BASE_URL,
});

// Vue 프로토타입에 apiClient 등록 (이제 모든 컴포넌트에서 this.$axios로 인스턴스 사용 가능)
Vue.prototype.$axios = apiClient;

// 2. 요청 인터셉터: 매 요청마다 최신 토큰 삽입
apiClient.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem('token');
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    },
    (error) => Promise.reject(error)
);

// 3. 라우터 정의 (순서 중요: 인터셉터에서 사용하기 위해 먼저 선언)
const routes = [
    {
        path: '/',
        name: 'Home',
        beforeEnter: (to, from, next) => {
            const token = localStorage.getItem('token');
            token ? next('/dashboard') : next('/login');
        }
    },
    { path: '/login', component: LoginPage },
    {
        path: '/register',
        name: 'Register',
        component: RegisterPage,
        beforeEnter: (to, from, next) => {
            const rawValue = localStorage.getItem('userGroupType');
            const userGroupType = rawValue ? rawValue.replace(/['"]+/g, '').trim() : '';
            if (userGroupType === 'admin') {
                next();
            } else if (!rawValue) {
                alert('로그인이 필요합니다.');
                next('/login');
            } else {
                alert('관리자 권한이 필요합니다.');
                next('/');
            }
        }
    },
    { path: '/invoice', name: 'Invoice', component: InvoicePage, meta: { requiresAuth: true } },
    { path: '/base/provider', name: 'Provider', component: ProviderPage, meta: { requiresAuth: true } },
    { path: '/base/customer', name: 'Customer', component: CustomerPage, meta: { requiresAuth: true } },
    { path: '/mypage', component: MyPage, meta: { requiresAuth: true } },
    { path: '/dashboard', component: MainDashboardPage, meta: { requiresAuth: true } },
    { path: '/Reset-Password', component: ResetPasswordPage },
    {
        path: '/account-book',
        name: 'AccountBook',
        component: AccountBookPage,
        meta: { requiresAuth: true }
    }
]

const router = new VueRouter({
    mode: 'history',
    base: process.env.BASE_URL,
    routes
})

// 4. 응답 인터셉터: 401 에러 발생 시 처리
apiClient.interceptors.response.use(
    (response) => response,
    async (error) => {
        const originalRequest = error.config;
        console.log("인터셉터 진입 성공! 상태코드:", error.response?.status);
        if ((error && error.status === 401) && (!originalRequest._retry)) {
            originalRequest._retry = true;
            try {
                // 2. 저장소에서 Refresh Token을 꺼냅니다.
                const refreshToken = localStorage.getItem('refreshToken');

                // 3. 백엔드에 새 Access Token을 요청합니다.
                // 주의: 이때는 인터셉터가 없는 순수 axios를 써야 무한 루프에 안 빠집니다.
                const res = await axios.post('/api/auth/refresh', {
                    refreshToken: refreshToken
                });
                if (res.data.status === "success") {
                    const newAccessToken = res.data.data.token;

                    // 4. 새 토큰을 다시 저장
                    localStorage.setItem('token', newAccessToken);

                    // 5. 실패했던 원래 요청의 헤더를 새 토큰으로 갈아끼우고 재실행
                    originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
                    return apiClient(originalRequest);
                }
            } catch (refreshError) {
                console.warn('토큰 만료 - 세션 정보를 삭제합니다.');
                // Refresh Token마저 만료되었거나 문제가 있다면 로그아웃 처리
                localStorage.removeItem('token');
                localStorage.removeItem('userGroupType'); // 관련 정보 함께 삭제 권장
                localStorage.removeItem('refreshToken');

                // 로그인 페이지 이동 및 알림
                if (router.currentRoute.path !== '/login') {
                    alert('세션이 만료되었습니다. 다시 로그인해주세요.');
                    router.push('/login');
                    return Promise.reject(refreshError);
                }
            }
        }
        return Promise.reject(error);
    }
);

// 5. 접근 제어 (Global Navigation Guard)
router.beforeEach((to, from, next) => {
    const isLoggedIn = !!localStorage.getItem('token');
    if (to.matched.some(record => record.meta.requiresAuth)) {
        if (!isLoggedIn) {
            alert('로그인이 필요한 서비스입니다.');
            next('/login');
        } else {
            next();
        }
    } else {
        next();
    }
})

// 중복 네비게이션 에러 방지
const originalPush = router.push;
router.push = function push(location) {
    return originalPush.call(this, location).catch(err => {
        if (err.name !== 'NavigationDuplicated' && !err.message.includes('Redirected')) {
            throw err;
        }
    });
};

export default router; // export default는 파일당 하나만!
